import React, {
    useCallback, useContext, useMemo, useState
} from 'react'
import { useDispatch } from 'react-redux'
import { _, colors } from 'utils'
import styled from 'styled-components'
import DataGrid, { ColumnDataGrid, DataGridProps } from 'components/DataGrid'
import Icon from 'components/Icon'
import FullPageLoading from 'components/FullPageLoading'
import { UserType } from 'generated/graphql'
import placeholderImg from 'assets/mugshot-placeholder.png'
import { GroupSessionContext } from '../GroupSessionEditor'
import { CardHeader } from '../../../components'
import Button from '../../../components/Button'
import { addModal } from '../../modals/modalSlice'
import AddParticipant from '../AddParticipant'
import { MAX_PARTICIPANTS } from '../GroupSessionList'
import { removeGroupSessionAttendee } from '../graphql/mutation'
import CollapsiblePanel from '../../../components/CollapsiblePanel'

const S = {
    TopToolbar: styled.div`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        width: 100%;
    `,
    Body: styled.div`
        width: 100%;
        padding: 0 20px;
    `,
    InfoWrapper: styled.div`
        display: flex;
        align-items: center;
        min-width: 0;
        max-width: 900px;
        overflow: hidden;
        padding-left: 8px;
    `,
    Avatar: styled.div`
        background: #ffffff;
        border-radius: 50%;
        height: 40px;
        width: 40px;
        min-width: 40px;
        margin-right: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    `,
    Thumbnail: styled.img`
        height: 40px;
        width: 40px;
    `,
    Title: styled.div`
        height: 40px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        overflow: hidden;
    `,
    Name: styled.div`
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        color: ${colors.grayTitle};
    `,
    Date: styled.div`
        display: flex;
        align-items: center;
    `,
    Icon: styled(Icon)`
        margin-top: -2px;
        margin-right: 5px;
    `,
    About: styled.div`
        height: 16px;
        margin-top: 6px;
        color: ${colors.grayDescription};
        font-size: 14px;
        line-height: 16px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        > {
            * {
                display: inline;
            }
            br {
                display: none;
            }
        }
    `,
    Action: styled.div`
        text-align: right;
        white-space: nowrap;
        padding-right: 8px;
    `,
}

const createColumns = ({ onDelete }): ColumnDataGrid[] => [
    {
        name: 'info',
        width: 11,
        render: (participant: UserType) => {
            const isPending = !participant.hasLoggedOn
            return (
                <S.InfoWrapper>
                    <S.Avatar>
                        <S.Thumbnail src={participant.mugshot || placeholderImg} alt="" />
                    </S.Avatar>
                    <S.Title>
                        <S.Name>
                            {isPending ? participant.email : `${participant.firstName} ${participant.lastName}`}
                        </S.Name>
                        {participant.about && (
                            <S.About dangerouslySetInnerHTML={{ __html: participant.about }} />
                        )}
                    </S.Title>
                </S.InfoWrapper>
            )
        }
    },
    {
        name: 'actions',
        width: 1,
        render: (participant: UserType) => (
            <S.Action>
                <Icon icon="trash" className="ml-2" size={20} onClick={() => onDelete(participant.id)} />
            </S.Action>
        )
    }
]

type ParticipantDataGridProps = Omit<DataGridProps, 'data'|'columns'> & {
    title: string
    participants: Partial<UserType>[]
    onDelete: (id: number) => void
}
const ParticipantDataGrid = ({
    title,
    participants,
    onDelete,
    ...props
}: ParticipantDataGridProps) => {
    const columns = useMemo(() => createColumns({ onDelete }), [onDelete])

    return (
        <CollapsiblePanel title={title}>
            <DataGrid
                data={participants}
                columns={columns}
                hideHeader
                hideAction
                {...props}
            />
        </CollapsiblePanel>
    )
}

const Participants = () => {
    const [loading, setLoading] = useState(false)
    const { groupSession, refresh } = useContext(GroupSessionContext)
    const dispatch = useDispatch()

    const showAddModal = useCallback(() => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <AddParticipant close={close} afterSubmit={refresh} groupSessionId={`${groupSession?.id}`} />
            ),
            shouldNotWrap: true
        }))
    }, [dispatch, groupSession?.id, refresh])
    const handleDelete = useCallback(async (id) => {
        if (!groupSession?.id) {
            return
        }

        setLoading(true)
        try {
            await removeGroupSessionAttendee({
                groupSessionId: `${groupSession.id}`,
                userId: id
            })
            refresh()
            return true
        } finally {
            setLoading(false)
        }
    }, [groupSession?.id, refresh])

    const isMaxParticipant = useMemo(() => groupSession
      && groupSession.participants.length >= MAX_PARTICIPANTS, [groupSession])

    return (
        <>
            <S.TopToolbar>
                <CardHeader>
                    {_('group_sessions.participants.list_header')}
                </CardHeader>
                <Button
                    icon="plusBox"
                    size="sm"
                    onClick={showAddModal}
                    disabled={loading || isMaxParticipant}
                >
                    {_('group_sessions.participants.add')}
                </Button>
            </S.TopToolbar>
            {loading && <FullPageLoading />}
            <S.Body>
                {!loading && (
                    <ParticipantDataGrid
                        title={_('group_sessions.participants.other_participants')}
                        participants={groupSession.participants}
                        onDelete={handleDelete}
                    />
                )}
            </S.Body>
        </>
    )
}

export default Participants
